import React, { useEffect, useState } from 'react';
import {
  Card, CardContent, Typography, Box, Grid, Chip, CircularProgress, Stack, Divider,
  Accordion, AccordionSummary, AccordionDetails, Table, TableBody, TableRow, TableCell,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import adminService from '../../services/adminService';
import ZimbabweProvinceMap from './ZimbabweProvinceMap';

export default function RegionalDistributionPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const result = await adminService.getDistribution();
        if (active) setData(result);
      } catch (err) {
        if (active) setError(err?.response?.data?.error || 'Failed to load distribution');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading) {
    return <Card><CardContent><Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box></CardContent></Card>;
  }
  if (error || !data) {
    return <Card><CardContent><Typography color="text.secondary">{error || 'No distribution data.'}</Typography></CardContent></Card>;
  }

  const chartData = data.provinces.map((p) => ({ province: p.province, contractors: p.contractors, verifiers: p.verifiers }));
  if (data.unspecified && data.unspecified.total > 0) {
    chartData.push({ province: 'Unspecified', contractors: data.unspecified.contractors, verifiers: data.unspecified.verifiers });
  }
  const { noContractors = [], noVerifiers = [] } = data.gaps || {};
  const districtGaps = data.districtGaps || { noContractors: [], noVerifiers: [] };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Regional Distribution</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Contractors and verifiers by province &amp; district
          ({data.totals?.contractors ?? 0} contractors, {data.totals?.verifiers ?? 0} verifiers).
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Box sx={{ height: 360 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="province" angle={-40} textAnchor="end" interval={0} height={70} tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="contractors" fill="#1976d2" name="Contractors" />
                  <Bar dataKey="verifiers" fill="#2e7d32" name="Verifiers" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Grid>

          <Grid item xs={12} md={5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Province coverage gaps</Typography>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="caption" color="text.secondary">Provinces with no contractors</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {noContractors.length ? noContractors.map((p) => <Chip key={p} size="small" color="warning" variant="outlined" label={p} />)
                    : <Chip size="small" color="success" variant="outlined" label="All covered" />}
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Provinces with no verifiers</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {noVerifiers.length ? noVerifiers.map((p) => <Chip key={p} size="small" color="warning" variant="outlined" label={p} />)
                    : <Chip size="small" color="success" variant="outlined" label="All covered" />}
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Districts with no contractors: {districtGaps.noContractors.length} of {data.provinces.flatMap((p) => p.districts).length}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>District drill-down</Typography>
            {data.provinces.map((p) => {
              const districtsWithUsers = p.districts.filter((d) => d.total > 0);
              const emptyDistricts = p.districts.filter((d) => d.total === 0).map((d) => d.district);
              return (
                <Accordion key={p.province} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ flex: 1, fontWeight: 500 }}>{p.province}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {p.contractors} C · {p.verifiers} V
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {districtsWithUsers.length > 0 ? (
                      <Table size="small">
                        <TableBody>
                          {districtsWithUsers.map((d) => (
                            <TableRow key={d.district}>
                              <TableCell>{d.district}</TableCell>
                              <TableCell align="right">{d.contractors} contractor{d.contractors === 1 ? '' : 's'}</TableCell>
                              <TableCell align="right">{d.verifiers} verifier{d.verifiers === 1 ? '' : 's'}</TableCell>
                            </TableRow>
                          ))}
                          {p.unspecifiedDistrict.total > 0 && (
                            <TableRow>
                              <TableCell sx={{ fontStyle: 'italic' }}>District unspecified</TableCell>
                              <TableCell align="right">{p.unspecifiedDistrict.contractors} C</TableCell>
                              <TableCell align="right">{p.unspecifiedDistrict.verifiers} V</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No providers in this province yet.</Typography>
                    )}
                    {emptyDistricts.length > 0 && (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">Uncovered districts ({emptyDistricts.length}):</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {emptyDistricts.map((d) => <Chip key={d} size="small" variant="outlined" label={d} />)}
                        </Box>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Coverage map</Typography>
            <ZimbabweProvinceMap data={data.provinces} />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
